import { useEffect, useRef, useState } from "react";

import { tgWebApp } from "../../../lib/tg";
import { userApi } from "../lib/api";
import type { PreCheckStatus, RollingPreCheckOut } from "../lib/types";

// Keep the first membership checks responsive, then let an unchanged open
// WebView settle to a five-minute cadence. Explicit verify/click paths remain
// immediate and any resolved progress resets the delay to the base interval.
const MAX_BACKOFF_MS = 300_000;
export const MAX_RETRY_AFTER_MS = 300_000;
const JITTER_RATIO = 0.2;
const MAX_TIMER_MS = 2_147_000_000;

type PollingOptions = {
  /** Capability fence: explicit verification reads durable GET state only. */
  enabled?: boolean;
  /**
   * `undefined` keeps the compatibility behaviour for isolated hook users.
   * An empty array means that the showcase has not loaded yet, so polling is
   * not useful. A populated array lets the hook stop after every card reaches
   * a terminal state.
   */
  relevantResourceIds?: readonly number[];
  /** Start a fresh request now without discarding any resolved card. */
  pollNowKey?: number;
  /** Forget one superseded negative while preserving every other resource. */
  resetResourceId?: number;
  resetResourceKey?: number;
  /** Accept only an authoritative MEMBER -> NOT_MEMBER downgrade. */
  allowMemberRegression?: boolean;
  /** Legacy flows stop once every current card is terminal. */
  stopWhenAllTerminal?: boolean;
};

// Polling deliberately slows down while the state does not change. This keeps
// an open Telegram WebView from producing a permanent five-second request loop.
export function usePreCheckPolling(
  flowToken: string,
  intervalMs: number = 5000,
  options: PollingOptions = {},
) {
  const [statuses, setStatuses] = useState<Record<number, PreCheckStatus>>({});
  const enabled = options.enabled ?? true;
  const relevantResourceKey = options.relevantResourceIds?.join(",");
  const pollNowKey = options.pollNowKey ?? 0;
  const resetResourceId = options.resetResourceId;
  const resetResourceKey = options.resetResourceKey ?? 0;
  const allowMemberRegression = options.allowMemberRegression ?? false;
  const stopWhenAllTerminal = options.stopWhenAllTerminal ?? true;
  const snapshotRef = useRef<{
    flowToken: string;
    relevantResourceKey: string | undefined;
    resetResourceKey: number;
    statuses: Record<number, PreCheckStatus>;
  } | null>(null);

  useEffect(() => {
    if (!enabled) {
      snapshotRef.current = {
        flowToken,
        relevantResourceKey,
        resetResourceKey,
        statuses: {},
      };
      setStatuses({});
      return;
    }

    let cancelled = false;
    let stopped = false;
    let timeoutId: number | undefined;
    let controller: AbortController | undefined;
    let requestGeneration = 0;
    const sameScope =
      snapshotRef.current?.flowToken === flowToken &&
      snapshotRef.current.relevantResourceKey === relevantResourceKey;
    const resetOneResource =
      allowMemberRegression &&
      sameScope &&
      snapshotRef.current?.resetResourceKey !== resetResourceKey &&
      resetResourceId !== undefined;
    let currentStatuses: Record<number, PreCheckStatus> = sameScope
      ? (snapshotRef.current?.statuses ?? {})
      : {};
    if (resetOneResource) {
      currentStatuses = { ...currentStatuses };
      delete currentStatuses[resetResourceId];
    }
    let backoffMs = Math.max(1, intervalMs);
    let retryNotBeforeMs = 0;
    const relevantResourceIds = parseResourceIds(relevantResourceKey);
    const webApp = tgWebApp();
    let telegramActive = webApp?.isActive !== false;
    if (!sameScope || resetOneResource) {
      snapshotRef.current = {
        flowToken,
        relevantResourceKey,
        resetResourceKey,
        statuses: currentStatuses,
      };
      setStatuses(currentStatuses);
    }

    function clearTimer() {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    }

    function schedule(clientDelayMs: number) {
      if (cancelled || stopped || document.hidden || !telegramActive) return;
      clearTimer();
      const delayMs = pollingDelayMs(
        clientDelayMs,
        Math.max(0, retryNotBeforeMs - Date.now()),
        Math.random(),
      );
      timeoutId = window.setTimeout(
        () => void tick(),
        Math.min(MAX_TIMER_MS, delayMs),
      );
    }

    async function tick() {
      if (
        cancelled ||
        stopped ||
        document.hidden ||
        !telegramActive ||
        controller
      )
        return;
      const generation = ++requestGeneration;
      const requestController = new AbortController();
      controller = requestController;
      try {
        const r = await userApi.getPreCheck(
          flowToken,
          requestController.signal,
        );
        if (cancelled || generation !== requestGeneration) return;
        const responseRetryAfterMs = retryAfterMs(r.response);
        if (responseRetryAfterMs > 0) {
          retryNotBeforeMs = Math.max(
            retryNotBeforeMs,
            Date.now() + responseRetryAfterMs,
          );
        }
        const data: RollingPreCheckOut | undefined = r.data;
        if (data) {
          const next = Object.fromEntries(
            Object.entries(data.statuses).map(([k, v]) => [Number(k), v]),
          );
          const mergedStatuses = mergePreCheckSnapshot(
            currentStatuses,
            next,
            relevantResourceIds,
            data.current_resource_ids,
            allowMemberRegression,
          );
          const progressed = hasResolvedProgress(
            currentStatuses,
            mergedStatuses,
          );
          currentStatuses = mergedStatuses;
          snapshotRef.current = {
            flowToken,
            relevantResourceKey,
            resetResourceKey,
            statuses: currentStatuses,
          };
          setStatuses(currentStatuses);
          if (
            stopWhenAllTerminal &&
            allRelevantStatusesTerminal(currentStatuses, relevantResourceIds)
          ) {
            stopped = true;
            return;
          }
          const currentDelay = progressed ? Math.max(1, intervalMs) : backoffMs;
          backoffMs = progressed
            ? Math.max(1, intervalMs)
            : nextBackoff(backoffMs, intervalMs);
          schedule(currentDelay);
          return;
        }
      } catch {
        if (
          cancelled ||
          generation !== requestGeneration ||
          requestController.signal.aborted
        )
          return;
        // A transient polling/rate-limit failure must not reset an already
        // resolved card or surface as an unhandled browser rejection.
      } finally {
        if (generation === requestGeneration) controller = undefined;
      }

      if (!cancelled && !stopped && generation === requestGeneration) {
        const currentDelay = backoffMs;
        backoffMs = nextBackoff(backoffMs, intervalMs);
        schedule(currentDelay);
      }
    }

    function abortActiveRequest() {
      requestGeneration += 1;
      const activeController = controller;
      controller = undefined;
      activeController?.abort();
    }

    function onVisibilityChange() {
      if (document.hidden) {
        clearTimer();
        abortActiveRequest();
        return;
      }
      if (!cancelled && !stopped) {
        // A user returning from Telegram is a strong signal that membership
        // may have changed. Re-check promptly even if passive polling already
        // reached its long-lived cap; an absolute Retry-After still wins.
        schedule(Math.max(1, intervalMs));
      }
    }

    function onTelegramActivated() {
      telegramActive = true;
      if (!cancelled && !stopped && !document.hidden) {
        schedule(Math.max(1, intervalMs));
      }
    }

    function onTelegramDeactivated() {
      telegramActive = false;
      clearTimer();
      abortActiveRequest();
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    webApp?.onEvent?.("activated", onTelegramActivated);
    webApp?.onEvent?.("deactivated", onTelegramDeactivated);
    if (relevantResourceIds === undefined || relevantResourceIds.length > 0) {
      void tick();
    }
    return () => {
      cancelled = true;
      clearTimer();
      abortActiveRequest();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      webApp?.offEvent?.("activated", onTelegramActivated);
      webApp?.offEvent?.("deactivated", onTelegramDeactivated);
    };
  }, [
    allowMemberRegression,
    enabled,
    flowToken,
    intervalMs,
    relevantResourceKey,
    pollNowKey,
    stopWhenAllTerminal,
    resetResourceId,
    resetResourceKey,
  ]);

  const pendingReset =
    enabled &&
    allowMemberRegression &&
    resetResourceId !== undefined &&
    snapshotRef.current?.flowToken === flowToken &&
    snapshotRef.current.relevantResourceKey === relevantResourceKey &&
    snapshotRef.current.resetResourceKey !== resetResourceKey;
  if (!pendingReset || !(resetResourceId in statuses)) return statuses;

  // Effects run after paint. Hide only the superseded snapshot immediately so
  // a successful repeat verification cannot flash the old NOT_MEMBER for one
  // render; every other card remains authoritative while the fresh poll runs.
  const visibleStatuses = { ...statuses };
  delete visibleStatuses[resetResourceId];
  return visibleStatuses;
}

function nextBackoff(currentMs: number, intervalMs: number): number {
  return Math.min(
    MAX_BACKOFF_MS,
    Math.max(Math.max(1, intervalMs), currentMs * 2),
  );
}

export function pollingDelayMs(
  clientDelayMs: number,
  retryAfterDelayMs: number,
  randomValue: number,
): number {
  const boundedClientDelay = Math.min(
    MAX_BACKOFF_MS,
    Math.max(1, clientDelayMs),
  );
  const boundedRandom = Math.min(1, Math.max(0, randomValue));
  const jitteredClientDelay = Math.min(
    MAX_BACKOFF_MS,
    Math.round(boundedClientDelay * (1 + JITTER_RATIO * boundedRandom)),
  );
  return Math.max(jitteredClientDelay, Math.max(0, retryAfterDelayMs));
}

export function retryAfterMs(response: Response | undefined): number {
  const value = response?.headers.get("Retry-After")?.trim();
  if (!value) return 0;
  if (/^\d+$/.test(value)) {
    return Math.min(MAX_RETRY_AFTER_MS, Number(value) * 1000);
  }
  const retryAt = Date.parse(value);
  return Number.isFinite(retryAt)
    ? Math.min(MAX_RETRY_AFTER_MS, Math.max(0, retryAt - Date.now()))
    : 0;
}

function parseResourceIds(value: string | undefined): number[] | undefined {
  if (value === undefined) return undefined;
  if (value === "") return [];
  return value.split(",").map(Number);
}

export function hasResolvedProgress(
  current: Record<number, PreCheckStatus>,
  merged: Record<number, PreCheckStatus>,
): boolean {
  return Object.entries(merged).some(([rawResourceId, mergedStatus]) => {
    const currentStatus = current[Number(rawResourceId)];
    return (
      currentStatus !== mergedStatus &&
      (mergedStatus === "member" ||
        mergedStatus === "not_member" ||
        mergedStatus === "system_bot_revoked")
    );
  });
}

export function allRelevantStatusesTerminal(
  statuses: Record<number, PreCheckStatus>,
  relevantResourceIds: readonly number[] | undefined,
): boolean {
  return Boolean(
    relevantResourceIds?.length &&
    relevantResourceIds.every((resourceId) => {
      const status = statuses[resourceId];
      return status === "member" || status === "system_bot_revoked";
    }),
  );
}

export function mergePreCheckStatuses(
  current: Record<number, PreCheckStatus>,
  next: Record<number, PreCheckStatus>,
  allowMemberRegression: boolean = false,
): Record<number, PreCheckStatus> {
  const merged = { ...current };
  for (const [rawResourceId, nextStatus] of Object.entries(next)) {
    const resourceId = Number(rawResourceId);
    const currentStatus = current[resourceId];
    if (
      currentStatus === "member" &&
      (!allowMemberRegression || nextStatus !== "not_member")
    )
      continue;
    if (
      nextStatus === "pending" &&
      currentStatus &&
      currentStatus !== "pending" &&
      currentStatus !== "error"
    )
      continue;
    merged[resourceId] = nextStatus;
  }
  return merged;
}

/**
 * Merge membership values and reconcile them with the authoritative resource
 * set returned by the same successful pre-check response.
 *
 * `system_bot_revoked` is the existing terminal/non-rendered client status. It
 * is also used as a local tombstone for resources that became unavailable for
 * any reason; the actual reason is deliberately not exposed by this public
 * endpoint. If a resource becomes current again before polling stops, the
 * authoritative snapshot revives it with its new status.
 */
export function mergePreCheckSnapshot(
  current: Record<number, PreCheckStatus>,
  next: Record<number, PreCheckStatus>,
  relevantResourceIds: readonly number[] | undefined,
  currentResourceIds: readonly number[] | undefined,
  allowMemberRegression: boolean = false,
): Record<number, PreCheckStatus> {
  const merged = mergePreCheckStatuses(current, next, allowMemberRegression);
  if (relevantResourceIds === undefined || currentResourceIds === undefined) {
    return merged;
  }

  const currentResourceSet = new Set(currentResourceIds);
  for (const resourceId of relevantResourceIds) {
    if (!currentResourceSet.has(resourceId)) {
      merged[resourceId] = "system_bot_revoked";
      continue;
    }
    if (current[resourceId] === "system_bot_revoked") {
      merged[resourceId] = next[resourceId] ?? "pending";
    }
  }
  return merged;
}

// Хелпер для оптимистичного обновления локального снапшота статусов
// (используется, когда юзер нажал «я подписался» и мы хотим сразу показать pending,
// не дожидаясь следующего тика поллинга).
export function setLocalStatus(
  prev: Record<number, PreCheckStatus>,
  resourceId: number,
  status: PreCheckStatus,
): Record<number, PreCheckStatus> {
  if (prev[resourceId] === "member" && status !== "member") return prev;
  return { ...prev, [resourceId]: status };
}
